import { useEffect, useId, useState, type ReactElement } from 'react';

import { useToast } from '@/components/ui/toast';
import { ModalShell } from '@/components/ui/ModalShell';
import { ApiError, apiErrorReason } from '@/lib/api';
import { stageKindLabel } from '@/lib/stage-kind';

import { hoursText, parseHours, standardVerb, workLabel } from '../standards-form';
import { STANDARD_STAGES, type StandardStage, type StandardsRow } from '../types';
import { useAdjustStandard } from '../use-standards';

// Ajuste del estándar de un tipo de trabajo (so=420, spec §4.2; so=340 §5): horas por
// etapa y motivo obligatorio. El cambio rige para los trabajos que empiecen desde ahora.

type Texts = Record<StandardStage, string>;

const textsOf = (row: StandardsRow): Texts =>
  Object.fromEntries(
    STANDARD_STAGES.map((s) => [s, hoursText(row.stages[s]?.standardMinutes ?? null)]),
  ) as Texts;

const NO_CHANGE = 'Las horas son iguales a las vigentes: no hay cambios que guardar.';
const BAD_HOURS =
  'Indica un valor mayor que cero y hasta 1000 horas, o deja vacío para quitar el estándar.';

interface Props {
  row: StandardsRow | null;
  companyId: string;
  onClose: () => void;
}

export function AdjustStandardModal({ row, companyId, onClose }: Props): ReactElement | null {
  const adjust = useAdjustStandard(companyId);
  const { showToast } = useToast();
  const titleId = useId();
  const [texts, setTexts] = useState<Texts | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTexts(row === null ? null : textsOf(row));
    setReason('');
    setError(null);
  }, [row]);

  if (row === null || texts === null) {
    return null;
  }
  const label = workLabel(row.documentTypeName, row.flowKind);
  const verb = standardVerb(row);
  const parsed = STANDARD_STAGES.map((s) => [s, parseHours(texts[s])] as const);
  const invalid = parsed.some(([, p]) => !p.ok);
  const canSave = !invalid && reason.trim().length >= 3 && !adjust.isPending;

  const save = async (): Promise<void> => {
    setError(null);
    const stages = Object.fromEntries(
      parsed.map(([s, p]) => [s, p.ok ? p.minutes : null]),
    ) as Record<StandardStage, number | null>;
    try {
      await adjust.mutateAsync({
        documentTypeId: row.documentTypeId,
        flowKind: row.flowKind,
        stages,
        reason: reason.trim(),
      });
      showToast(`Estándar de ${label} ${verb === 'Definir' ? 'definido' : 'actualizado'}`);
      onClose();
    } catch (e) {
      const noChange =
        e instanceof ApiError && e.status === 409 && apiErrorReason(e) === 'no_change';
      setError(noChange ? NO_CHANGE : 'No se pudo guardar el estándar. Vuelve a intentar.');
    }
  };

  return (
    <ModalShell
      open
      title={`${verb} estándar de ${label}`}
      busy={adjust.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={adjust.isPending}
          >
            Cancelar
          </button>
          <button type="button" className="btn sm" onClick={() => void save()} disabled={!canSave}>
            {adjust.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <p className="soft" style={{ fontSize: 12.5, marginBottom: 12 }}>
          El cambio rige para los trabajos que empiecen desde ahora; los que ya están en curso
          conservan el estándar con que empezaron. Deja una etapa vacía para quitarle el estándar.
        </p>
        <div className="stdgrid">
          {STANDARD_STAGES.map((s) => (
            <label key={s} className="fld">
              <span className="fld-lab">{`${stageKindLabel(s)} (horas)`}</span>
              <input
                type="text"
                inputMode="decimal"
                className="selector fw"
                value={texts[s]}
                onChange={(e) => setTexts({ ...texts, [s]: e.target.value })}
              />
            </label>
          ))}
        </div>
        {invalid && (
          <p className="fld-err" role="alert">
            {BAD_HOURS}
          </p>
        )}
        <label
          htmlFor={`${titleId}-r`}
          className="fld-lab"
          style={{ display: 'block', marginTop: 12 }}
        >
          Motivo del ajuste (obligatorio)
        </label>
        <textarea
          id={`${titleId}-r`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Por qué cambia el estándar (queda en el historial)"
          style={{ width: '100%', marginTop: 6 }}
        />
        {error !== null && (
          <p className="fld-err" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
